import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";
import { useDeleteScene } from "../hooks";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  sceneId: string;
};

const DeleteSceneDialog = ({ isOpen, onClose, sceneId }: Props) => {
  const navigate = useNavigate();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const { isPending, isSuccess, error, deleteScene } = useDeleteScene(sceneId);

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          {!error && !isSuccess && (
            <>
              <AlertDialogHeader fontSize="lg" fontWeight="semibold">
                Delete Scene {sceneId?.toString()}?
              </AlertDialogHeader>
              <AlertDialogBody>
                By choosing to delete this scene, all the scene information will be removed from
                the database, as well as its detections, model runs and uploaded files.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button isDisabled={isPending} variant="base-text" ref={cancelRef} onClick={onClose}>
                  No
                </Button>
                <Button
                  isLoading={isPending}
                  loadingText="Deleting"
                  variant="solid"
                  colorScheme="red"
                  onClick={deleteScene}
                  ml={3}
                >
                  Yes, delete scene
                </Button>
              </AlertDialogFooter>
            </>
          )}
          {error && (
            <>
              <AlertDialogHeader fontSize="lg" fontWeight="semibold">
                Error
              </AlertDialogHeader>
              <AlertDialogBody>{error.message}</AlertDialogBody>
              <AlertDialogFooter>
                <Button variant="base-outline" ref={cancelRef} onClick={onClose}>
                  Close
                </Button>
              </AlertDialogFooter>
            </>
          )}
          {isSuccess && (
            <>
              <AlertDialogHeader fontSize="lg" fontWeight="semibold">
                Success
              </AlertDialogHeader>
              <AlertDialogBody>Scene deleted successfully.</AlertDialogBody>
              <AlertDialogFooter>
                <Button variant="base-outline" ref={cancelRef} onClick={() => navigate("/scenes")}>
                  Close
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default DeleteSceneDialog;
